import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PlayerState } from "./types";

const getPreferencesFromLocalStorage = (): Record<
  string,
  number | undefined
> | null => JSON.parse(localStorage.getItem("preferences") || "null");

const getConfigFromSearchParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const fromTime = searchParams.get("from");
  const toTime = searchParams.get("to");
  const cameraId = searchParams.get("camera");
  const isLive = searchParams.get("is_live") === "true";
  const alignSegment = searchParams.get("align_segment") === "true";
  const timeshift = searchParams.get("timeshift");
  const liveDelay = searchParams.get("live_delay");
  const seekThreshold = searchParams.get("seek");
  const padding = searchParams.get("padding");
  const zoom = searchParams.get("zoom");

  return {
    fromTime,
    toTime,
    cameraId,
    isLive,
    alignSegment,
    timeshift,
    liveDelay,
    seekThreshold,
    padding,
    zoom,
  };
};

const defaultPreferences = {
  liveDelayThreshold: 15, // in seconds
  seekThreshold: 1000, // in milliseconds
  timeshift: 18_000, // in seconds
  padding: 900, // in seconds
};

const initialState = (): PlayerState => {
  const {
    cameraId,
    fromTime,
    toTime,
    isLive,
    alignSegment,
    timeshift,
    liveDelay,
    seekThreshold,
    padding,
    zoom,
  } = getConfigFromSearchParams();

  const savedPreferences = getPreferencesFromLocalStorage();
  const defaultLive = isLive && !fromTime && !toTime ? true : false;

  return {
    currentTime: null,
    liveEdge: null,
    zoom: zoom ? parseFloat(zoom) : 0.5,
    videoStream: {
      cameraId: cameraId ? cameraId : null,
      fromTime: fromTime ? fromTime : "",
      toTime: toTime ? toTime : "",
      isLive: isLive ? isLive : defaultLive,
      alignSegment: alignSegment ? alignSegment : false,
      availabilityStartTime: null,
      computedAvailabilityEndTime: null,
      arbitraryFromTime: null,
      arbitraryToTime: null,
    },
    segments: [],
    preferences: {
      liveDelayThreshold: liveDelay
        ? parseInt(liveDelay)
        : savedPreferences?.liveDelayThreshold ||
          defaultPreferences.liveDelayThreshold,
      seekThreshold: seekThreshold
        ? parseInt(seekThreshold)
        : savedPreferences?.seekThreshold || defaultPreferences.seekThreshold,
      timeshift: timeshift
        ? parseInt(timeshift)
        : savedPreferences?.timeshift || defaultPreferences.timeshift,
      padding: padding
        ? parseInt(padding)
        : savedPreferences?.padding || defaultPreferences.padding,
    },
  };
};

export const playerSlice = createSlice({
  name: "player",
  initialState,
  selectors: {
    isPlayerIdle: (state) => {
      return (
        !state.videoStream.fromTime &&
        !state.videoStream.toTime &&
        !state.videoStream.isLive &&
        !state.videoStream.arbitraryFromTime &&
        !state.videoStream.arbitraryToTime
      );
    },
    hasOnlyFilterRange: (state) => {
      return (
        !state.videoStream.isLive &&
        state.videoStream.fromTime &&
        state.videoStream.toTime &&
        !state.videoStream.arbitraryFromTime &&
        !state.videoStream.arbitraryToTime
      );
    },
    hasArbitraryRange: (state) => {
      return (
        state.videoStream.arbitraryFromTime && state.videoStream.arbitraryToTime
      );
    },
    canStartVideo: (state) => {
      const {
        isLive,
        fromTime,
        toTime,
        arbitraryFromTime,
        arbitraryToTime,
        cameraId,
      } = state.videoStream;

      return (
        !!cameraId &&
        (isLive ||
          !!(fromTime && toTime) ||
          !!(arbitraryFromTime && arbitraryToTime))
      );
    },
    selectAvailabilityStartTime: (state) =>
      state.videoStream.availabilityStartTime,
    selectComputedAvailabilityEndTime: (state) =>
      state.videoStream.computedAvailabilityEndTime,
    selectFromTime: (state) => state.videoStream.fromTime,
    selectToTime: (state) => state.videoStream.toTime,
    selectIsLive: (state) => state.videoStream.isLive,
    selectArbitraryFromTime: (state) => state.videoStream.arbitraryFromTime,
    selectArbitraryToTime: (state) => state.videoStream.arbitraryToTime,
    selectCameraId: (state) => state.videoStream.cameraId,
    selectSegments: (state) => state.segments,
  },
  reducers: {
    updateCurrentTime: (state, action) => {
      state.currentTime = action.payload;
    },
    updateLiveEdge: (state, action) => {
      state.liveEdge = action.payload;
    },
    updateZoom: (state, action) => {
      state.zoom = action.payload;
    },
    updateVideoStream: (
      state,
      action: PayloadAction<Partial<PlayerState["videoStream"]>>
    ) => {
      state.videoStream = {
        ...state.videoStream,
        ...action.payload,
      };
    },
    updatePreferences: (
      state,
      action: PayloadAction<Partial<PlayerState["preferences"]>>
    ) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload,
      };
    },
    setSegments: (state, action: PayloadAction<PlayerState["segments"]>) => {
      state.segments = action.payload;
    },
  },
});

export const {
  updateCurrentTime,
  updateLiveEdge,
  updateZoom,
  updateVideoStream,
  updatePreferences,
  setSegments,
} = playerSlice.actions;

export const {
  isPlayerIdle,
  hasOnlyFilterRange,
  hasArbitraryRange,
  canStartVideo,
  selectAvailabilityStartTime,
  selectComputedAvailabilityEndTime,
  selectFromTime,
  selectToTime,
  selectIsLive,
  selectArbitraryFromTime,
  selectArbitraryToTime,
  selectCameraId,
  selectSegments,
} = playerSlice.selectors;
